import React, { useEffect, useRef } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import { MB_TextInput } from '@/components/general/text-input';
import { MB_Tags, MB_Tag } from '@/components/general/tags';
import { mbTextStyles } from '@/utils/general/style-utils';
import { adminApiCalls } from '../../../apiCalls/adminApiCalls';
import { COLORS } from '../../../constants/colors';
import { textStyles } from '../../../constants/textStyles';
import { RESOURCE_POOL_TYPE } from '../../../typesAndInterfaces/typesAndInterfaces';
import { BulkUploadItem, BULK_UPLOAD_DUPLICATE_CHECK_DEBOUNCE_MS, BULK_UPLOAD_ITEM_STATUS } from './bulkUploadTypes';

interface BulkUploadItemRowProps {
  item: BulkUploadItem;
  resourcePoolType: RESOURCE_POOL_TYPE;
  /**
   * True when another item in the batch has the same (trimmed, case-insensitive) name.
   */
  isDuplicateInBatch: boolean;
  isUploading: boolean;
  onItemChange: (id: string, patch: Partial<BulkUploadItem>) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

/**
 * One row of the bulk-upload review list: editable name with live duplicate warning,
 * per-file tags, status/progress and remove/retry controls.
 */
const BulkUploadItemRow = ({ item, resourcePoolType, isDuplicateInBatch, isUploading, onItemChange, onRemove, onRetry }: BulkUploadItemRowProps) => {
  const poolType = resourcePoolType === RESOURCE_POOL_TYPE.Image ? 'image' : 'video';
  const duplicateCheckTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { id, name, status } = item;

  // Live duplicate warning: debounce the name and re-ask the server whenever it changes,
  // so the warning updates as the admin edits. Purely advisory here — upload re-checks.
  useEffect(() => {
    if (status === BULK_UPLOAD_ITEM_STATUS.uploading || status === BULK_UPLOAD_ITEM_STATUS.success) {
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }
    clearTimeout(duplicateCheckTimer.current);
    duplicateCheckTimer.current = setTimeout(async () => {
      try {
        const isDuplicate = await adminApiCalls.checkPoolDuplicate(poolType, trimmedName);
        onItemChange(id, { isDuplicateOnServer: isDuplicate });
      } catch {
        // Advisory only — a failed check should not block editing.
      }
    }, BULK_UPLOAD_DUPLICATE_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(duplicateCheckTimer.current);
  }, [id, name, poolType, status, onItemChange]);

  const isDone = status === BULK_UPLOAD_ITEM_STATUS.success;
  const showDuplicateWarning = !isDone && (item.isDuplicateOnServer || isDuplicateInBatch);

  return (
    <View style={[styles.row, isDone && styles.rowSuccess, status === BULK_UPLOAD_ITEM_STATUS.error && styles.rowError]} testID={`bulk-upload-row-${id}`}>
      <View style={styles.rowMain}>
        <View style={styles.nameColumn}>
          <Text style={styles.fileName} numberOfLines={1}>
            {item.file.fileName}
          </Text>
          <MB_TextInput
            value={item.name}
            onChangeText={newName => onItemChange(id, { name: newName, showNameError: false, isDuplicateOnServer: false })}
            isError={item.showNameError}
            disable={isDone || status === BULK_UPLOAD_ITEM_STATUS.uploading}
            placeholder={`${poolType === 'image' ? 'Image' : 'Video'} name*`}
            style={styles.nameInput}
          />
          {showDuplicateWarning && (
            <Text style={styles.duplicateWarning}>{isDuplicateInBatch ? 'Another file in this batch has the same name.' : `A ${poolType} with this name already exists in the pool.`}</Text>
          )}
          {item.showNameError && !item.name.trim() && <Text style={styles.duplicateWarning}>Name is required.</Text>}
        </View>

        <View style={styles.tagsColumn}>
          <MB_Tags
            onTagsChange={(newTags: MB_Tag[]) => onItemChange(id, { tags: newTags.map(tag => tag.title), showTagsError: false })}
            title=""
            maxTagsLength={5}
            maxTagLength={20}
            containerStyle={[styles.tagsContainer, item.showTagsError && styles.tagsContainerError]}
          />
          {item.showTagsError && <Text style={styles.duplicateWarning}>At least 1 tag is required.</Text>}
        </View>

        <View style={styles.statusColumn}>
          {status === BULK_UPLOAD_ITEM_STATUS.ready && <Text style={styles.statusText}>Ready</Text>}
          {status === BULK_UPLOAD_ITEM_STATUS.uploading && (
            <View>
              <Text style={styles.statusText}>{item.progress}%</Text>
              <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${item.progress}%` }]} />
              </View>
            </View>
          )}
          {isDone && (
            <View style={styles.statusWithIcon}>
              <Feather name="check-circle" color={COLORS.green} size={16} />
              <Text style={[styles.statusText, styles.statusSuccessText]}> Uploaded</Text>
            </View>
          )}
          {status === BULK_UPLOAD_ITEM_STATUS.error && (
            <View>
              <Text style={[styles.statusText, styles.statusErrorText]} numberOfLines={2}>
                {item.errorMessage ?? 'Upload failed'}
              </Text>
              <TouchableOpacity onPress={() => onRetry(id)} disabled={isUploading} style={styles.retryButton} testID={`bulk-upload-retry-${id}`}>
                <Feather name="rotate-ccw" color={COLORS.buttonPurple} size={14} />
                <Text style={styles.retryText}> Retry</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <TouchableOpacity
          onPress={() => onRemove(id)}
          disabled={isUploading || status === BULK_UPLOAD_ITEM_STATUS.uploading}
          style={styles.removeButton}
          testID={`bulk-upload-remove-${id}`}
        >
          <Feather name="x" color={isUploading ? COLORS.textLightPurple : '#e64545'} size={18} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export { BulkUploadItemRow };

const styles = StyleSheet.create({
  row: {
    borderWidth: 1,
    borderColor: COLORS.textLightPurple,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
  },
  rowSuccess: {
    borderColor: '#3BB273',
  },
  rowError: {
    borderColor: '#e64545',
  },
  rowMain: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  nameColumn: {
    flex: 3,
    marginEnd: 12,
  },
  fileName: mbTextStyles([
    textStyles.smallText,
    {
      textAlign: 'left',
      color: COLORS.textLightPurple,
      marginBottom: 4,
    },
  ]),
  nameInput: {
    width: '100%',
  },
  duplicateWarning: mbTextStyles([
    textStyles.smallText,
    {
      textAlign: 'left',
      color: '#e6a245',
      marginTop: 4,
    },
  ]),
  tagsColumn: {
    flex: 3,
    marginEnd: 12,
  },
  tagsContainer: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  tagsContainerError: {
    borderColor: '#e64545',
  },
  statusColumn: {
    flex: 2,
    justifyContent: 'center',
    minHeight: 40,
  },
  statusText: mbTextStyles([
    textStyles.smallText,
    {
      textAlign: 'left',
    },
  ]),
  statusSuccessText: {
    color: '#3BB273',
  },
  statusErrorText: {
    color: '#e64545',
  },
  statusWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#3d3654',
    marginTop: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: COLORS.buttonPurple,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  retryText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.buttonPurple,
    },
  ]),
  removeButton: {
    padding: 4,
    marginStart: 4,
  },
});
